<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-ovalSettings-styleAndAnimation"
  >
    <opacity-settings v-model="opacity" />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Background color</label>
      <label
        class="radio inline-radio"
        for="solid"
      >Solid
        <input
          id="solid"
          v-model="fillStyle"
          type="radio"
          value="solid"
          data-test="graphics-ovalSettings-fillStyleSolid"
        />
        <div class="checkmark"></div>
      </label>
      <label
        class="radio inline-radio"
        for="gradient"
      >Gradient
        <input
          id="gradient"
          v-model="fillStyle"
          type="radio"
          value="gradient"
          data-test="graphics-ovalSettings-fillStyleGradient"
        />
        <div class="checkmark"></div>
      </label>

      <div
        v-if="fillStyle == 'solid'"
        class="colorpicker text-center"
        data-test="graphics-ovalSettings-fillStyleSolidColor"
      >
        <colorpicker
          v-model="backgroundColor"
          :is-mapped="checkMapped('backgroundColor')"
        />
        <span
          data-balloon="This setting is controlled by data. Unmap data to customize."
          data-balloon-pos="down"
          data-balloon-length="medium"
        >
          <span
            v-if="checkMapped('backgroundColor')"
            class="fa fa-lock mapped-lock"
          />
        </span>
      </div>

      <div
        v-else
        class="settings-row settings-row-xs"
        data-test="graphics-ovalSettings-fillStyleGradient"
      >
        <div class="settings-col-6 settings-col-xs">
          <div class="input-group">
            <validation-field as="input"
              v-model.number="gradientDirection"
              rules="required|numeric"
              type="number"
              name="gradientDirection"
              :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
              data-test="graphics-ovalSettings-setGradientDirection"
              data-entity="graphics-ovalSettings-setGradientDirection"
            />
            <span class="input-group-addon symbol">°</span>
          </div>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorOne"
            :is-mapped="checkMapped('gradientColorOne')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorOne')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorTwo"
            :is-mapped="checkMapped('gradientColorTwo')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down-right"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorTwo')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
      </div>

      <!-- TODO: remove once sheets can have more than one thing mapped -->
      <div
        v-if="showDataMapControls"
        class="margin-top-sm"
        data-test="graphics-ovalSettings-fillStyleGradientMapped"
      >
        <p
          v-if="isBackgroundColorMapped"
          class="sidebar-form-label margin-top-sm"
          data-test="graphics-ovalSettings-fillStyleGradientMappedFromData"
        >
          From Data Provider
        </p>

        <map-data-control
          v-if="fillStyle == 'solid'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="backgroundColor"
          :mapped="checkMapped('backgroundColor')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="gradientColorOne"
          :mapped="checkMapped('gradientColorOne')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="gradientColorTwo"
          :mapped="checkMapped('gradientColorTwo')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>
    </div>

    <hr class="sidebar-divider" />

    <background-image-settings
      :element="element"
      :scroll-offset="scrollOffset"
      @modal="(opts) => $emit('modal', opts)"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Border
        <input
          v-model="border"
          type="checkbox"
          data-test="graphics-ovalSettings-toggleBorder"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <template v-if="border">
        <div class="settings-row settings-row-xs">
          <div class="settings-col-6 settings-col-xs">
            <validation-field as="input"
              v-model.number="borderWidth"
              rules="required|numeric|min:0"
              type="number"
              name="borderWidth"
              min="0"
              :class="{ 'is-invalid': validationErrors['borderWidth'] }"
              data-test="graphics-ovalSettings-setBorderWidth"
              data-entity="graphics-ovalSettings-setBorderWidth"
            />
            <label class="sidebar-input-sub-label">Width</label>
          </div>
          <div class="settings-col-6 settings-col-xs colorpicker">
            <colorpicker
              v-model="borderColor"
              :is-mapped="checkMapped('borderColor')"
            />
            <label class="sidebar-input-sub-label">
              Color
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down-right"
                data-balloon-length="medium"
              >
                <span
                  v-if="checkMapped('borderColor')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </label>
          </div>
        </div>
        <!-- TODO: remove once sheets can have more than one thing mapped -->
        <div
          v-if="showDataMapControls"
          data-test="graphics-ovalSettings-borderMapped"
        >
          <p
            v-if="checkMapped('borderColor')"
            class="sidebar-form-label margin-bottom-sm"
            data-test="graphics-ovalSettings-borderMappedFromData"
          >
            From Data Provider
          </p>
          <map-data-control
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="borderColor"
            :mapped="checkMapped('borderColor')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
        </div>
      </template>
    </div>

    <hr class="sidebar-divider" />

    <box-shadow-settings
      :element="element"
      :data-provider="dataProvider"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Blur
        <input
          v-model="blur"
          type="checkbox"
          data-test="graphics-ovalSettings-toggleBlur"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div
        v-if="blur"
        class="input-group"
      >
        <validation-field as="input"
          v-model.number="blurSize"
          rules="required|numeric|min:0"
          type="number"
          name="blurSize"
          min="0"
          :class="{ 'is-invalid': validationErrors['blurSize'] }"
          data-test="graphics-ovalSettings-setBlurSize"
          data-entity="graphics-ovalSettings-setBlurSize"
        />
        <span class="input-group-addon">px</span>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import BackgroundImageSettings from './BackgroundImage.vue';
import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import MapDataControl from './Data/MapDataControl.vue';

export default {
  emits: ['modal'],
  name: 'OvalSettingsComponent',
  components: {
    AnimationSettings,
    BackgroundImageSettings,
    BoxShadowSettings,
    OpacitySettings,
    MapDataControl,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],
  computed: {
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },
};
</script>
